import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createRoute, Navigate } from '@tanstack/react-router'
import { Save } from 'lucide-react'
import { type FormEvent, useEffect, useState } from 'react'
import { PersonnelLayout } from '../components/personnel-layout'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { api } from '../lib/api'
import { notify } from '../lib/notify'
import { Route as rootRoute } from './__root'

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/vacation-rules',
  component: VacationRulesPage,
})

function VacationRulesPage() {
  const queryClient = useQueryClient()
  const { data: meData, isLoading: meLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.me(),
  })
  const isAdmin = meData?.user?.role === 'admin'

  const { data: rulesData, isLoading: rulesLoading } = useQuery({
    queryKey: ['vacation-rules'],
    queryFn: () => api.getVacationRules(),
    enabled: !!meData?.user,
  })

  const [minLead, setMinLead] = useState('0')
  const [maxConsecutive, setMaxConsecutive] = useState('0')
  // Globale Ist-Zeit-Schalter (app_settings.count_travel/meetings_in_actual).
  // Steuern, ob der Kalender Anfahrt bzw. Meetings zur Ist-Zeit zählt. Die
  // Felder liefert derselbe vacation-rules-Endpoint; der VacationRules-Typ (den
  // ein anderer Cluster besitzt) kennt sie noch nicht → defensiv gelesen.
  const [countMeetings, setCountMeetings] = useState(false)

  useEffect(() => {
    if (rulesData) {
      setMinLead(String(rulesData.rules.vacationMinLeadDays))
      setMaxConsecutive(String(rulesData.rules.vacationMaxConsecutiveDays))
      const r = rulesData.rules as {
        countMeetingsInActual?: boolean
      }
      setCountMeetings(!!r.countMeetingsInActual)
    }
  }, [rulesData])

  const save = useMutation({
    mutationFn: () =>
      api.updateVacationRules({
        vacationMinLeadDays: Number(minLead) || 0,
        vacationMaxConsecutiveDays: Number(maxConsecutive) || 0,
        // Nicht im VacationRules-Typ (fremder Cluster) — der Endpoint akzeptiert
        // die beiden Booleans aber bereits (PATCH /app-settings/vacation-rules).
        countMeetingsInActual: countMeetings,
      } as Parameters<typeof api.updateVacationRules>[0]),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vacation-rules'] })
      notify.success('Urlaubsregeln gespeichert')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  if (meLoading) return null
  if (!meData?.user) return <Navigate to="/login" />

  if (!isAdmin) {
    return (
      <PersonnelLayout title="Urlaubsregeln">
        <div className="rounded-2xl border border-border-subtle bg-bg-elevated/40 p-8 text-center">
          <p className="text-text-secondary">
            Die Urlaubsregeln können nur Administratoren bearbeiten.
          </p>
        </div>
      </PersonnelLayout>
    )
  }

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    const a = Number(minLead)
    const b = Number(maxConsecutive)
    if (!Number.isInteger(a) || a < 0 || a > 365) {
      notify.error('Vorlaufzeit muss zwischen 0 und 365 Tagen liegen')
      return
    }
    if (!Number.isInteger(b) || b < 0 || b > 365) {
      notify.error('Maximale Dauer muss zwischen 0 und 365 Tagen liegen')
      return
    }
    save.mutate()
  }

  return (
    <PersonnelLayout
      title="Urlaubsregeln"
      description="Vorlaufzeit für Urlaubsanträge und maximale Urlaubsdauer am Stück. 0 = keine Einschränkung. Admins umgehen beide Regeln."
    >
      <form
        onSubmit={onSubmit}
        className="max-w-xl space-y-6 rounded-2xl border border-border-subtle bg-bg-elevated/40 p-6"
      >
        <div className="space-y-2">
          <Label htmlFor="min-lead">Mindest-Vorlaufzeit (Tage)</Label>
          <Input
            id="min-lead"
            type="number"
            min={0}
            max={365}
            value={minLead}
            onChange={(e) => setMinLead(e.target.value)}
            disabled={rulesLoading}
          />
          <p className="text-xs text-text-tertiary">
            Urlaubsanträge müssen mindestens diese Anzahl Tage vor dem Beginn eingereicht werden. 0
            = sofort möglich.
          </p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="max-consecutive">Maximale Dauer am Stück (Tage)</Label>
          <Input
            id="max-consecutive"
            type="number"
            min={0}
            max={365}
            value={maxConsecutive}
            onChange={(e) => setMaxConsecutive(e.target.value)}
            disabled={rulesLoading}
          />
          <p className="text-xs text-text-tertiary">
            Maximale Länge eines einzelnen Urlaubsantrags in Kalendertagen. 0 = keine Beschränkung.
            Längere Urlaube müssen in mehrere Anträge aufgeteilt werden.
          </p>
        </div>
        <div className="space-y-3 border-t border-border-subtle pt-6">
          <p className="text-sm font-medium text-text-primary">Ist-Zeit-Berechnung</p>
          <p className="text-xs text-text-tertiary">
            Vorbelegung für neue Meetings („zählt als Arbeitszeit"). Reisezeit wird pro Zeiteintrag
            entschieden — die Checkbox dafür erscheint direkt am Eintrag, sobald Reisezeit erfasst
            ist.
          </p>
          {/* Touch-first: das ganze Label ist klickbar (min. 44px Trefferfläche
              durch py-2), die native Checkbox dient nur als visueller Zustand. */}
          <label className="flex cursor-pointer select-none items-center gap-3 py-2">
            <input
              type="checkbox"
              checked={countMeetings}
              onChange={(e) => setCountMeetings(e.target.checked)}
              disabled={rulesLoading}
              className="h-4 w-4 shrink-0 accent-accent"
            />
            <span className="text-sm">
              <span className="text-text-primary">
                Neue Meetings zählen standardmäßig als Arbeitszeit
              </span>
              <span className="block text-xs text-text-tertiary">
                Nur der Vorschlag beim Anlegen — pro Meeting über die Checkbox „Zählt als
                Arbeitszeit" übersteuerbar.
              </span>
            </span>
          </label>
        </div>
        <div className="flex justify-end">
          <Button type="submit" disabled={save.isPending}>
            <Save className="h-4 w-4" />
            Speichern
          </Button>
        </div>
      </form>
    </PersonnelLayout>
  )
}
